import { Settings } from "luxon";
import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { UserDto } from "../../DTOs/User/UserDto";

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: null,
  isTimezoneLoading: false,
  timezoneFetchError: null
});

type TimezoneContextType = {
  timezone: string | null;
  isTimezoneLoading: boolean;
  timezoneFetchError: string | null
};

export const TimezoneProvider: React.FC<{ children: React.ReactNode, user: UserDto | null }> = ({ children, user }) => {
  const { data: timezone, isLoading, error } = useQuery({
    queryKey: ['timezoneSettings', user?.id ?? null],
    queryFn: async () => {
      const response = await settingsAjax.getTimezoneSettings();
      if (ErrorResponse.isErrorResponse(response)) {
        console.error("Failed to fetch timezone");
        throw new Error(response.messages[0].description);
      }
      Settings.defaultZone = response.timezoneId;
      return response.timezoneId;
    },
    enabled: !!user,
    retry: false,
  });

  const memoizedValue = useMemo(() => ({
    timezone: timezone ?? null,
    isTimezoneLoading: isLoading,
    timezoneFetchError: error ? (error instanceof Error ? error.message : "Failed to fetch timezone info") : null
  }), [timezone, isLoading, error]);

  return (
    <TimezoneContext.Provider value={memoizedValue}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => useContext(TimezoneContext);
